import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { triggerAIAnalysisForJob } from "./useJobsData";

export const useAddJob = () => {
	const addJob = async (jobData: any): Promise<string | undefined> => {
		if (!jobData.title || !jobData.companyId) {
			toast({
				title: "Error",
				description: "Title and company are required fields",
				variant: "destructive",
			});
			return undefined;
		}

		try {
			const { data: sessionData } = await supabase.auth.getSession();
			const userUuid = sessionData.session?.user?.id;

			let organizationUuid = null;
			if (userUuid) {
				const { data: userOrg } = await supabase
					.from('user_organization')
					.select('organization_uuid')
					.eq('user_uuid', userUuid)
					.single();
				organizationUuid = userOrg?.organization_uuid;
			}

			const skillsArray = jobData.skills
				? Array.isArray(jobData.skills) ? jobData.skills
					: typeof jobData.skills === 'string' ? jobData.skills.split(',').map((s: string) => s.trim())
						: []
				: [];

			const insertData = {
				title: jobData.title,
				company_id: jobData.companyId,
				description: jobData.description || null,
				skills: skillsArray.length > 0 ? skillsArray : null,
				contact_id: jobData.contactId || null,
				organization_uuid: organizationUuid,
				...(jobData.status && { status: jobData.status }),
				...(jobData.employment_type && { employment_type: jobData.employment_type }),
				...(jobData.time_commitment && { time_commitment: jobData.time_commitment }),
				...(jobData.contract_duration && { contract_duration: jobData.contract_duration }),
				...(jobData.start_date && { start_date: jobData.start_date }),
				...(jobData.end_date && { end_date: jobData.end_date }),
				...(jobData.seniority_level && { seniority_level: jobData.seniority_level }),
				...(jobData.budget && { budget: jobData.budget }),
				...(jobData.budget_freelance && { budget_freelance: jobData.budget_freelance }),
				...(jobData.location_type && { location_type: jobData.location_type }),
				...(jobData.workplace_breakdown && { workplace_breakdown: jobData.workplace_breakdown }),
				...(jobData.hard_skills && { hard_skills: jobData.hard_skills }),
				...(jobData.soft_skills && { soft_skills: jobData.soft_skills }),
				...(jobData.education_level && { education_level: jobData.education_level }),
				...(jobData.years_of_experience && { years_of_experience: jobData.years_of_experience }),
				...(jobData.language_requirements && { language_requirements: jobData.language_requirements }),
				...(jobData.extra_lang && { extra_lang: jobData.extra_lang }),
				...(jobData.culture_tags && { culture_tags: jobData.culture_tags }),
				...(jobData.application_deadline && { application_deadline: jobData.application_deadline }),
				...(jobData.job_posting_date && { job_posting_date: jobData.job_posting_date }),
				...(jobData.source && { source: jobData.source }),
				...(jobData.assigned_recruiter && { assigned_recruiter: jobData.assigned_recruiter }),
				...(jobData.employment_classification && { employment_classification: jobData.employment_classification }),
				...(jobData.ai_summary && { ai_summary: jobData.ai_summary }),
			};

			const { data, error } = await supabase
				.from('jobs')
				.insert(insertData)
				.select()
				.single();

			if (error) {
				console.error("Supabase error when adding job:", error);
				throw error;
			}

			await triggerAIAnalysisForJob(data.id);
			document.location.href = `/jobs/${data.id}`;

			toast({
				title: "Success",
				description: "Job offer has been created successfully",
			});

			return data.id;
		} catch (error) {
			console.error("Error adding job:", error);
			toast({
				title: "Error",
				description: "Failed to create job offer: " + (error instanceof Error ? error.message : "Unknown error"),
				variant: "destructive",
			});
			return undefined;
		}
	};

	return { addJob };
};
